import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	ViewEncapsulation,
} from '@angular/core';
import {
	CustomerDTO,
	DATE_MDY,
	DATE_YMD,
	DropdownItem,
	GET_LOAD_VOLUME,
	GET_PROJECTED_MARGIN,
	INTEGRATION_TYPE,
	IS_EMPTY,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	Owner,
	ProjectDTO,
	TO_DOLLARS,
	TO_CENTS,
} from 'cargo-chief-commons';
import { ACTIVE_USERS, ALL_ACCOUNT_USERS } from '@shared/utils/account-users';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { TOOLTIP_MSG, OFFER_RATE_CONFIRMATION } from '@domain/constants/app-variables';
import { AccessCacheService } from '@service/access-cache/access-cache.service';
import { UntypedFormControl, Validators } from '@angular/forms';
import { ProjectHelperService } from '@service/projectHelper/project-helper.service';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { UtilService } from '@service/util/util.service';
import { BaConfigService } from '@service/baConfig/ba-config.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ConfirmationDialogComponent } from '../../../common/confirmation-dialog/confirmation-dialog.component';
import { LaneEditDialogComponent, LaneEditDialogData } from '../../lane-edit-dialog/lane-edit-dialog.component';

import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { PROJECT_TYPES, IS_WORKFLOW_PROJECT_TYPE } from '@domain/constants/project-types';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { IntegrationService } from '@service/integration/integration.service';

// Base class only — the retired CURRENT layout was its last render site. All business
// logic here is inherited by ProjectSpecificationsV2Component, which owns the live template.
@Component({
	selector: 'app-project-specifications',
	templateUrl: './project-specifications.component.html',
	styleUrls: ['./project-specifications.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class ProjectSpecificationsComponent implements OnInit, OnChanges {
	@Input() project: ProjectDTO = null;
	@Input() isUpdatingProject: boolean = false;
	@Input() projectGroups: any[] = [];

	@Output() updateProjectEvent: EventEmitter<ProjectDTO> = new EventEmitter();

	usersList: Array<Owner> = null;
	allUsersList: Array<Owner> = null;
	projectCopy: ProjectDTO = null;
	minimumDueDate = new Date();
	dueDate = new UntypedFormControl(null, Validators.required);
	PROJECT_STAGES: Array<any> = [];
	loadVolumeDuration: Array<string> = [];
	daysRun: string = '';
	targetMargin: number = null;
	additionalNotes: string[] = [];
	showNotes: boolean = false;
	DAYS: Array<DropdownItem> = null;
	DAYS_RUN: Array<string> = [];
	IS_TEAMS_CAPACITY_ENABLED: boolean = false;
	IS_NOT_NULL_OR_UNDEFINED = IS_NOT_NULL_OR_UNDEFINED;
	IS_NULL_OR_UNDEFINED = IS_NULL_OR_UNDEFINED;
	IS_NOT_EMPTY = IS_NOT_EMPTY;
	IS_EMPTY = IS_EMPTY;
	GET_PROJECTED_MARGIN = GET_PROJECTED_MARGIN;
	TOOLTIP_MSG = TOOLTIP_MSG;
	projectEquipments: string[] = [];
	trailerTypes: any[] = [];
	projectTypes: any = PROJECT_TYPES;
	projectType: any;
	PROJECT_TYPES = PROJECT_TYPES;
	// Custom type options for Procurement page (shows "Workflow" which maps to WATERFALL)
	procurementTypeOptions = [
		{ label: 'Procurement', value: PROJECT_TYPES.PROCUREMENT.value },
		{ label: 'Workflow', value: PROJECT_TYPES.WATERFALL.value },
	];
	// Workflow page options - updated in initialSetup based on current type
	workflowTypeOptions = [];
	private debouncer: Subject<any> = new Subject<any>();
	private targetMarginChanged: Subject<any> = new Subject<any>();
	isWaterfallOrDedicatedLaneProject: boolean = false;
	isMcleodIntegrationEnabled: boolean = false;
	// Automated Carrier Negotiation (CN-8626) — per-workflow override of the account default.
	autoAcceptOverride: boolean = false;
	autoAcceptMarginDisplay: number = null;
	autoAcceptMarginPlaceholder: string = '%';

	// variables for component state
	isUserRoleAdmin: boolean = false;
	isUserProject: boolean = false;
	userPopoverComponent = {
		placeholder: '',
		selectedUsers: [],
		enabled: false,
	};
	environment = environment;

	constructor(
		public cargochief: Cargochief,
		public accountSettings: AccountSettingsService,
		public projectHelperService: ProjectHelperService,
		private accessCache: AccessCacheService,
		public utilService: UtilService,
		public baConfigService: BaConfigService,
		private integrationService: IntegrationService,
		private dialog: MatDialog
	) {
		this.trailerTypes = this.accountSettings.SEARCHABLE_TRAILER_TYPES;
		this.loadVolumeDuration = this.projectHelperService.getLoadVolumeDuration();
		this.IS_TEAMS_CAPACITY_ENABLED =
			environment.TEAMS_CAPACITY && this.accountSettings.TEAMS_CAPACITY;
		this.baConfigService.setBaConfig();
	}

	ngOnInit(): void {
		if (IS_NOT_NULL_OR_UNDEFINED(this.project)) {
			this.PROJECT_STAGES = this.projectHelperService.getProjectStages();
			this.DAYS = this.projectHelperService.days;
			this.initialSetup();
			this.calculateTargetMargin(this.project.maxRate, this.project.minRate);

			this.debouncer.pipe(debounceTime(1000)).subscribe((value) => {
				switch (value) {
					case 'minRate':
						this.maxBuyChanged();
						break;
					case 'maxRate':
						this.customerRateChanged();
						break;
					case 'packaging':
					case 'trailerRequirements':
					case 'shipperNotes':
					case 'receiverNotes':
						this.updateProject();
						break;
				}
			});

			this.targetMarginChanged.pipe(debounceTime(250)).subscribe((value) => {
				this.calculateMarginChange();
			});
		}
		this.setIsWaterfallOrDedicatedLaneProject();

		this.isUserRoleAdmin = this.utilService.isUserRoleAdmin();

		this.isMcleodIntegrationEnabled = this.integrationService.isIntegrationEnabled(
			INTEGRATION_TYPE.MCLEOD
		);
	}

	ngOnChanges() {
		this.initialSetup();
		if (IS_NOT_NULL_OR_UNDEFINED(this.projectCopy)) {
			this.isUserProject = this.cargochief.USER_ID == this.projectCopy.userId;
			// A non-ACTIVE assignee is absent from usersList, so resolve the name here.
			const assignee = this.allUsersList?.find(
				(user) => user.userId == this.projectCopy.assigneeId
			);
			this.userPopoverComponent.placeholder = assignee
				? assignee.name
				: IS_NULL_OR_UNDEFINED(this.projectCopy.assigneeId) &&
					this.cargochief.USER_ID != this.projectCopy.userId
					? 'Unassigned'
					: 'Assign User';
			this.userPopoverComponent.selectedUsers = IS_NULL_OR_UNDEFINED(this.projectCopy.assigneeId)
				? []
				: [this.projectCopy.assigneeId];
			this.userPopoverComponent.enabled = this.isUserProject || this.isUserRoleAdmin;
		}
		if (this.project) {
			this.setIsWaterfallOrDedicatedLaneProject();
		}
	}

	initialSetup() {
		this.populateNotesMetadata();
		this.DAYS_RUN = GET_LOAD_VOLUME(this.project).split(', ');
		this.dueDate = new UntypedFormControl(
			new Date(DATE_MDY(this.project.dueDate)),
			Validators.required
		);
		this.projectCopy = Object.assign({}, this.project);
		this.autoAcceptOverride =
			IS_NOT_NULL_OR_UNDEFINED(this.project.autoAcceptEnabled) ||
			IS_NOT_NULL_OR_UNDEFINED(this.project.autoAcceptMarginValue) ||
			IS_NOT_NULL_OR_UNDEFINED(this.project.autoAcceptMarginType) ||
			IS_NOT_NULL_OR_UNDEFINED(this.project.autoAcceptTimeXHours) ||
			IS_NOT_NULL_OR_UNDEFINED(this.project.autoAcceptBookEnabled);
		if (!this.projectCopy.autoAcceptMarginType) {
			this.projectCopy.autoAcceptMarginType = 'Percentage';
		}
		this.autoAcceptMarginPlaceholder =
			this.projectCopy.autoAcceptMarginType === 'Dollar' ? '$' : '%';
		this.autoAcceptMarginDisplay = this.autoAcceptStoredToDisplay(this.project);
		this.projectEquipments = this.projectCopy.equipmentType?.split(',') || [];
		this.usersList = ACTIVE_USERS(this.accessCache.getAccountUsers(), this.project.assigneeId);
		this.allUsersList = ALL_ACCOUNT_USERS(
			this.accessCache.getAccountUsers(),
			this.project.assigneeId
		);
		this.updateWorkflowTypeOptions();
	}

	// Updates workflow type options based on current project type
	updateWorkflowTypeOptions() {
		const currentType = this.projectCopy?.type;
		// Use current workflow type (WATERFALL or DEDICATED_LANE) for "Workflow" option
		const workflowValue = currentType === PROJECT_TYPES.DEDICATED_LANE.value
			? PROJECT_TYPES.DEDICATED_LANE.value
			: PROJECT_TYPES.WATERFALL.value;
		this.workflowTypeOptions = [
			{ label: 'Procurement', value: PROJECT_TYPES.PROCUREMENT.value },
			{ label: 'Workflow', value: workflowValue },
		];
	}

	// Dollar margins are persisted in cents; the form shows dollars.
	autoAcceptStoredToDisplay(p: ProjectDTO): number {
		if (IS_NULL_OR_UNDEFINED(p.autoAcceptMarginValue)) {
			return null;
		}
		return p.autoAcceptMarginType === 'Dollar' ? p.autoAcceptMarginValue / 100 : p.autoAcceptMarginValue;
	}

	autoAcceptMarginToStored(): number {
		if (IS_NULL_OR_UNDEFINED(this.autoAcceptMarginDisplay)) {
			return null;
		}
		return this.projectCopy.autoAcceptMarginType === 'Dollar'
			? Math.round(this.autoAcceptMarginDisplay * 100)
			: this.autoAcceptMarginDisplay;
	}

	onAutoAcceptOverrideChange(checked: boolean): void {
		this.autoAcceptOverride = checked;
		if (!checked) {
			this.autoAcceptMarginDisplay = null;
			return;
		}
		// Seed the per-workflow inputs from the account-level defaults so overriding starts
		// from the account values (resolution is workflow ?? account). Only fill fields the
		// workflow hasn't already set, so a previously-saved override keeps its own values.
		const accountDefaults = this.baConfigService.getAccountAutoAcceptDefaults();
		if (IS_NULL_OR_UNDEFINED(this.projectCopy.autoAcceptEnabled)) {
			this.projectCopy.autoAcceptEnabled = accountDefaults.enabled;
		}
		if (IS_NULL_OR_UNDEFINED(this.projectCopy.autoAcceptTimeXHours)) {
			this.projectCopy.autoAcceptTimeXHours = accountDefaults.timeXHours;
		}
		if (IS_NULL_OR_UNDEFINED(this.projectCopy.autoAcceptBookEnabled)) {
			// Seed the book opt-in from the account default so the checkbox reflects what it inherits — else it
			// renders unchecked while null silently inherits the account setting (which could be ON), booking loads.
			this.projectCopy.autoAcceptBookEnabled = accountDefaults.bookEnabled;
		}
		if (
			IS_NULL_OR_UNDEFINED(this.projectCopy.autoAcceptMarginValue) &&
			IS_NULL_OR_UNDEFINED(this.autoAcceptMarginDisplay)
		) {
			this.projectCopy.autoAcceptMarginType = accountDefaults.marginType;
			this.autoAcceptMarginDisplay = accountDefaults.marginDisplay;
		}
		if (!this.projectCopy.autoAcceptMarginType) {
			this.projectCopy.autoAcceptMarginType = 'Percentage';
		}
		this.autoAcceptMarginPlaceholder =
			this.projectCopy.autoAcceptMarginType === 'Dollar' ? '$' : '%';
	}

	onAutoAcceptMarginTypeChange(value: string): void {
		this.autoAcceptMarginPlaceholder = value === 'Dollar' ? '$' : '%';
		this.autoAcceptMarginDisplay = null;
	}

	isProjectUpdated(): boolean {
		return (
			(IS_NOT_NULL_OR_UNDEFINED(this.project) &&
				IS_NOT_NULL_OR_UNDEFINED(this.projectCopy) &&
				(this.project.name != this.projectCopy.name ||
					this.project.customerDTO?.name != this.projectCopy.customerDTO?.name ||
					this.project.assigneeId != this.projectCopy.assigneeId ||
					DATE_MDY(this.project.dueDate) != DATE_MDY(this.projectCopy.dueDate) ||
					this.project.stageId != this.projectCopy.stageId ||
					this.project.loadVolumeDuration != this.projectCopy.loadVolumeDuration ||
					this.project.minRate != this.projectCopy.minRate ||
					this.project.maxRate != this.projectCopy.maxRate ||
					this.project.offerRate != this.projectCopy.offerRate ||
					(IS_NOT_EMPTY(this.projectCopy.loadCount) &&
						this.project.loadCount != this.projectCopy.loadCount) ||
					(IS_NOT_EMPTY(this.projectCopy.commodity) &&
						this.project.commodity != this.projectCopy.commodity) ||
					(IS_NOT_EMPTY(this.projectCopy.weight) &&
						this.project.weight != this.projectCopy.weight) ||
					(IS_NOT_EMPTY(this.projectCopy.loadVolume) &&
						this.project.loadVolume != this.projectCopy.loadVolume) ||
					this.projectEquipments.join(',') != (this.projectCopy.equipmentType || '') ||
					this.project.inviteUngroupedCarriers != this.projectCopy.inviteUngroupedCarriers ||
					this.project.type != this.projectCopy.type ||
					this.project.revenueCode != this.projectCopy.revenueCode ||
					this.project.ungroupedCarriersDelay != this.projectCopy.ungroupedCarriersDelay ||
					this.project.maxBuy != this.projectCopy.maxBuy ||
					this.project.sendToOutOfNetworkCarriers != this.projectCopy.sendToOutOfNetworkCarriers ||
					this.project.sendToOutOfNetworkCarriersDelay !=
						this.projectCopy.sendToOutOfNetworkCarriersDelay)) ||
			this.project.appointmentRequired != this.projectCopy.appointmentRequired ||
			this.project.notifyProcuredCarriers != this.projectCopy.notifyProcuredCarriers ||
			this.project.assigneeIsDispatcher != this.projectCopy.assigneeIsDispatcher ||
			this.project.bookNowUpToMaxBuy != this.projectCopy.bookNowUpToMaxBuy ||
			this.project.originCity != this.projectCopy.originCity ||
			this.project.originState != this.projectCopy.originState ||
			this.project.destCity != this.projectCopy.destCity ||
			this.project.destState != this.projectCopy.destState ||
			this.project.autoAcceptEnabled !=
				(this.autoAcceptOverride ? this.projectCopy.autoAcceptEnabled : null) ||
			this.project.autoAcceptMarginType !=
				(this.autoAcceptOverride ? this.projectCopy.autoAcceptMarginType : null) ||
			this.project.autoAcceptTimeXHours !=
				(this.autoAcceptOverride ? this.projectCopy.autoAcceptTimeXHours : null) ||
			this.project.autoAcceptMarginValue !=
				(this.autoAcceptOverride ? this.autoAcceptMarginToStored() : null) ||
			this.project.autoAcceptBookEnabled !=
				(this.autoAcceptOverride ? this.projectCopy.autoAcceptBookEnabled : null)
		);
	}

	async updateProject() {
		const isNotifyProcuredCarriersUpdated: boolean =
			this.project.notifyProcuredCarriers != this.projectCopy.notifyProcuredCarriers;

		// Check if project type is changing from Workflow to Procurement with existing groups
		const isTypeTransitionToProcurement =
			this.project.type !== PROJECT_TYPES.PROCUREMENT.value &&
			this.projectCopy.type === PROJECT_TYPES.PROCUREMENT.value;
		const groupsLoaded = Array.isArray(this.projectGroups);
		const hasProjectGroups = groupsLoaded && this.projectGroups.length > 0;

		// Avoid treating "not loaded" as "empty"
		if (isTypeTransitionToProcurement && !groupsLoaded) {
			return;
		}

		if (isTypeTransitionToProcurement && hasProjectGroups) {
			const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
				width: '500px',
				data: {
					title: 'Change Project Type',
					body: 'Changing to Procurement will delete all existing carrier groups. Carriers will remain procured but will no longer be organized into priority groups. Are you sure you want to continue?',
					cancelButtonText: 'Cancel',
					ConfirmButtonText: 'Continue',
				},
				disableClose: true,
			});

			const result = await dialogRef.afterClosed().toPromise();
			if (!result) {
				this.projectCopy.type = this.project.type;
				this.updateWorkflowTypeOptions();
				return;
			}
		}

		// Check if offer rate is being set to 0
		if (this.projectCopy.offerRate === 0 && this.project.offerRate !== 0) {
			const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
				width: '500px',
				data: {
					title: OFFER_RATE_CONFIRMATION.TITLE,
					body: OFFER_RATE_CONFIRMATION.MESSAGE,
					cancelButtonText: OFFER_RATE_CONFIRMATION.CANCEL,
					ConfirmButtonText: OFFER_RATE_CONFIRMATION.CONFIRM,
				},
				disableClose: true,
			});

			const result = await dialogRef.afterClosed().toPromise();
			if (!result) {
				// User cancelled, revert the offer rate change
				this.projectCopy.offerRate = this.project.offerRate;
				return;
			}
		}

		// Persist the per-workflow auto-accept override (null = inherit account default;
		// Dollar margins in cents).
		if (this.autoAcceptOverride) {
			this.projectCopy.autoAcceptMarginValue = this.autoAcceptMarginToStored();
		} else {
			this.projectCopy.autoAcceptEnabled = null;
			this.projectCopy.autoAcceptMarginType = null;
			this.projectCopy.autoAcceptMarginValue = null;
			this.projectCopy.autoAcceptTimeXHours = null;
			this.projectCopy.autoAcceptBookEnabled = null;
		}
		this.project = this.projectCopy;
		let projectDTO: any = Object.assign({}, this.projectCopy);
		projectDTO.dueDate = DATE_YMD(this.dueDate.value);

		// Store the original offerRate value
		const originalOfferRate = projectDTO.offerRate;

		projectDTO = this.projectHelperService.convertProjectDollarsToCents(projectDTO);
		projectDTO.equipmentType = this.projectEquipments.join(',');
		projectDTO = this.projectHelperService.updateProjectDaysRun(projectDTO, this.DAYS_RUN);
		projectDTO.sendToNetworkCarriersDelay = projectDTO.ungroupedCarriersDelay;
		projectDTO.sendToNetworkCarriers = projectDTO.inviteUngroupedCarriers;
		projectDTO.lastUpdatedByUserId = this.cargochief.USER_ID;

		// Handle offerRate after conversion; Offer Rate == 0 is a valid special case
		if (originalOfferRate === '' || originalOfferRate === null) {
			projectDTO.offerRate = undefined;
		} else if (originalOfferRate === 0) {
			projectDTO.offerRate = 0;
		}

		if (isNotifyProcuredCarriersUpdated) {
			projectDTO.notifyProcuredCarriersLastUpdatedAt = DATE_YMD(new Date());
			projectDTO.notifyProcuredCarriersLastUpdatedByUserId = this.cargochief.USER_ID;
		}

		this.updateProjectEvent.emit(projectDTO);
	}

	setCustomer(selectedCustomer: CustomerDTO) {
		if (IS_NOT_NULL_OR_UNDEFINED(selectedCustomer) && IS_NOT_NULL_OR_UNDEFINED(this.projectCopy)) {
			this.projectCopy.customerDTO = selectedCustomer;
		}
	}

	assigneeUpdated(newAssignedUser: Owner) {
		if (IS_NOT_NULL_OR_UNDEFINED(newAssignedUser)) {
			this.projectCopy.assigneeId = newAssignedUser.userId;
		}
	}

	dueDateUpdated() {
		this.projectCopy.dueDate = this.dueDate.value;
	}

	async inputChanged(propertyKey: string) {
		if (
			propertyKey === 'offerRate' &&
			(this.projectCopy.offerRate === 0 ||
				(IS_NULL_OR_UNDEFINED(this.project.offerRate) && this.projectCopy.offerRate === 0))
		) {
			const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
				width: '500px',
				data: {
					title: OFFER_RATE_CONFIRMATION.TITLE,
					body: OFFER_RATE_CONFIRMATION.MESSAGE,
					cancelButtonText: OFFER_RATE_CONFIRMATION.CANCEL,
					ConfirmButtonText: OFFER_RATE_CONFIRMATION.CONFIRM,
				},
				disableClose: true,
			});

			const result = await dialogRef.afterClosed().toPromise();
			if (!result) {
				// User cancelled, revert the offer rate change
				this.projectCopy.offerRate = this.project.offerRate;
				return;
			}
		}

		this.debouncer.next(propertyKey);
	}

	calculateMarginChange() {
		const minRate = this.projectCopy.minRate,
			maxRate = this.projectCopy.maxRate;
		if (
			(this.projectHelperService.isGrossMarginValid(this.targetMargin) &&
				this.projectHelperService.isValidRateInput(minRate) &&
				this.projectHelperService.isValidRateInput(maxRate)) ||
			(this.projectHelperService.isGrossMarginValid(this.targetMargin) &&
				this.projectHelperService.isValidRateInput(maxRate))
		) {
			this.projectCopy.minRate = Math.round(((100 - this.targetMargin) * Number(maxRate)) / 100);
		} else if (
			this.projectHelperService.isGrossMarginValid(this.targetMargin) &&
			this.projectHelperService.isValidRateInput(minRate)
		) {
			this.projectCopy.maxRate = Math.round(
				(Number(minRate) * 100) / (100 - Number(this.targetMargin))
			);
		}
	}

	maxBuyChanged() {
		const minRate = this.projectCopy.minRate,
			maxRate = this.projectCopy.maxRate,
			currentMargin = this.targetMargin;
		if (
			this.projectHelperService.isGrossMarginValid(currentMargin) &&
			this.projectHelperService.isValidRateInput(minRate) &&
			this.projectHelperService.isValidRateInput(maxRate)
		) {
			this.projectCopy.maxRate = Math.round((Number(minRate) * 100) / (100 - this.targetMargin));
		} else if (
			!this.projectHelperService.isGrossMarginValid(currentMargin) &&
			this.projectHelperService.isValidRateInput(minRate) &&
			this.projectHelperService.isValidRateInput(maxRate)
		) {
			this.calculateTargetMargin(maxRate, minRate);
		}
	}

	customerRateChanged() {
		const minRate = this.projectCopy.minRate,
			maxRate = this.projectCopy.maxRate,
			currentMargin = this.targetMargin;
		if (
			this.projectHelperService.isGrossMarginValid(currentMargin) &&
			this.projectHelperService.isValidRateInput(minRate) &&
			this.projectHelperService.isValidRateInput(maxRate)
		) {
			this.projectCopy.minRate = Math.round(((100 - this.targetMargin) * Number(maxRate)) / 100);
		} else if (
			!this.projectHelperService.isGrossMarginValid(currentMargin) &&
			this.projectHelperService.isValidRateInput(minRate) &&
			this.projectHelperService.isValidRateInput(maxRate)
		) {
			this.calculateTargetMargin(maxRate, minRate);
		}
	}

	calculateTargetMargin(maxRate: number, minRate: number) {
		if (IS_NOT_NULL_OR_UNDEFINED(maxRate) && IS_NOT_NULL_OR_UNDEFINED(minRate)) {
			const calculatedMargin = ((maxRate - minRate) / maxRate) * 100;
			if (calculatedMargin > 0) {
				this.targetMargin = Math.round(calculatedMargin);
			}
		}
	}

	onTargetMarginChange(projectCopy: ProjectDTO) {
		this.targetMarginChanged.next(projectCopy);
	}

	async submitLastBid() {
		let result = await this.projectHelperService.openLastBidDialog();
		if (IS_NOT_NULL_OR_UNDEFINED(result)) {
			this.projectCopy.lastBid = result.lastBid;
			this.updateProject();
		}
	}

	populateNotesMetadata() {
		this.additionalNotes = [];
		if (IS_NOT_EMPTY(this.project.packaging)) {
			this.additionalNotes.push('Packaging');
		}
		if (IS_NOT_EMPTY(this.project.trailerRequirements)) {
			this.additionalNotes.push('Trailer Requirements');
		}
		if (IS_NOT_EMPTY(this.project.shipperNotes)) {
			this.additionalNotes.push('Shipper Notes');
		}
		if (IS_NOT_EMPTY(this.project.receiverNotes)) {
			this.additionalNotes.push('Receiver Notes');
		}
	}

	getFirstNoteSummary(): string {
		if (IS_NOT_EMPTY(this.projectCopy?.packaging)) {
			return this.projectCopy.packaging;
		}
		if (IS_NOT_EMPTY(this.projectCopy?.trailerRequirements)) {
			return this.projectCopy.trailerRequirements;
		}
		if (IS_NOT_EMPTY(this.projectCopy?.shipperNotes)) {
			return this.projectCopy.shipperNotes;
		}
		if (IS_NOT_EMPTY(this.projectCopy?.receiverNotes)) {
			return this.projectCopy.receiverNotes;
		}
		return 'No notes or instructions';
	}

	sendOfferSettingUpdated(event: any, delayFieldName: string) {
		if (event) {
			this.projectCopy[delayFieldName] = 5;
		} else {
			this.projectCopy[delayFieldName] = 0;
		}
	}

	setIsWaterfallOrDedicatedLaneProject() {
		this.isWaterfallOrDedicatedLaneProject = IS_WORKFLOW_PROJECT_TYPE(this.project?.type);
	}

	openLaneEditDialog() {
		const dialogRef = this.dialog.open(LaneEditDialogComponent, {
			width: '500px',
			data: {
				originCity: this.projectCopy.originCity,
				originState: this.projectCopy.originState,
				destCity: this.projectCopy.destCity,
				destState: this.projectCopy.destState,
				equipmentTypes: this.projectEquipments,
			} as LaneEditDialogData,
		});

		dialogRef.afterClosed().subscribe((result: LaneEditDialogData | null) => {
			if (result) {
				this.projectCopy.originCity = result.originCity;
				this.projectCopy.originState = result.originState;
				this.projectCopy.destCity = result.destCity;
				this.projectCopy.destState = result.destState;
				this.projectEquipments = result.equipmentTypes;
				this.updateProjectNameFromLane();
			}
		});
	}

	updateProjectNameFromLane() {
		let name = '';
		const originCityExist = IS_NOT_EMPTY(this.projectCopy.originCity);
		const originStateExist = IS_NOT_EMPTY(this.projectCopy.originState);
		const destCityExist = IS_NOT_EMPTY(this.projectCopy.destCity);
		const destStateExist = IS_NOT_EMPTY(this.projectCopy.destState);

		if (originCityExist) {
			name += this.projectCopy.originCity;
		}

		if (originStateExist) {
			name += originCityExist ? ', ' : '';
			name += this.projectCopy.originState;
		}

		const hasDestination = destCityExist || destStateExist;

		if (hasDestination && (originCityExist || originStateExist)) {
			name += ' → ';
		}

		if (destCityExist) {
			name += this.projectCopy.destCity;
		}

		if (destStateExist) {
			name += destCityExist ? ', ' : '';
			name += this.projectCopy.destState;
		}

		if (this.projectEquipments.length > 0) {
			name += originCityExist || originStateExist || destCityExist || destStateExist ? ' ' : '';
			name += '(' + this.projectEquipments.map((equip) => equip[0]).join(',') + ')';
		}

		if (IS_NOT_EMPTY(name)) {
			this.projectCopy.name = name;
		}
	}
}
